/**
 * Widget Dashboard "Mon stock bouteilles" — branché sur `planCave.statsResume`
 * (Lot D, plus de fixture STOCK_VIVANT).
 *
 * Affiche le stock bouteilles TOUJOURS ventilé par stade (règle D.0) : le
 * total n'apparaît qu'en complément de la ventilation. Barres de progression =
 * part de chaque stade dans le total. États : skeleton (chargement), message
 * honnête (erreur/droits), état vide avec lien vers le module Cave.
 */

import type { StatsResumeCaveSortie, StockParStade } from "@champ-erp/contracts";
import { Card, ProgressBar, Skeleton, WidgetHeader, type ProgressTone } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import { Wine } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { trpc } from "../../lib/trpc.js";
import {
  formatNbBouteilles,
  STADE_LABELS,
  STADES_ORDONNES,
  totalBouteilles,
} from "../cave/stades.js";

// Tons par stade : aubergine = en transformation (lattes/pointes), vine =
// dégorgées (livrables après habillage), gold = habillées (prêtes à vendre).
const STADE_TONES: Record<keyof StockParStade, ProgressTone> = {
  sur_lattes: "aubergine",
  sur_pointes: "aubergine",
  degorgee: "vine",
  habillee: "gold",
};

export function WidgetStockVivant() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["planCave.statsResume"],
    queryFn: () => trpc.planCave.statsResume.query(),
  });
  const stats = data as StatsResumeCaveSortie | undefined;

  return (
    <Card className="p-6">
      <WidgetHeader
        eyebrow="Cave · stock bouteilles"
        title="Mon stock bouteilles"
        action={{
          label: "Voir le détail",
          href: "/stock",
          // Navigation client (pas de full reload sur un lien interne).
          onClick: (event) => {
            event.preventDefault();
            navigate("/stock");
          },
        }}
      />
      {isLoading ? (
        <StockSkeleton />
      ) : isError || !stats ? (
        <p className="rounded-lg border border-dashed border-champagne-200 bg-champagne-50/40 px-4 py-6 text-center text-[13px] text-champagne-700">
          Impossible de charger le stock bouteilles pour l&apos;instant.
        </p>
      ) : (
        <StockContenu stats={stats} />
      )}
    </Card>
  );
}

function StockContenu({ stats }: { stats: StatsResumeCaveSortie }) {
  const stock = stats.nb_bouteilles_par_stade;
  const total = totalBouteilles(stock);

  // État vide propre : aucun lot en cave (0 partout) → invitation, pas de 0 %.
  if (total === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-champagne-50 text-champagne-700 ring-1 ring-champagne-200">
          <Wine size={26} />
        </div>
        <p className="text-[14px] text-champagne-800">
          Aucune bouteille en cave pour l&apos;instant.
        </p>
        <Link
          to="/cave"
          className="text-[13px] font-medium text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
        >
          Ouvrir le module Cave →
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Total EN COMPLÉMENT de la ventilation (jamais seul — règle D.0). */}
      <div className="mb-5 border-b border-champagne-900/10 pb-5 leading-tight">
        <div className="mono text-[13px] uppercase tracking-[0.12em] text-champagne-700">
          Bouteilles en cave (tous stades)
        </div>
        <div
          className="mono tnum mt-1.5 text-[28px] font-semibold"
          style={{ letterSpacing: "-0.01em" }}
        >
          {formatNbBouteilles(total)}{" "}
          <span className="text-[14px] font-normal text-champagne-700">bt</span>
        </div>
        <div className="mt-1.5 text-[12px] text-champagne-800">
          {formatNbBouteilles(stats.nb_lots_actifs)} lot{stats.nb_lots_actifs > 1 ? "s" : ""} actif
          {stats.nb_lots_actifs > 1 ? "s" : ""} · ventilé par stade
        </div>
      </div>
      <div className="space-y-4">
        {STADES_ORDONNES.map((stade) => (
          <StadeLigne
            key={stade}
            libelle={STADE_LABELS[stade]}
            nbBouteilles={stock[stade]}
            pct={Math.round((stock[stade] / total) * 100)}
            tone={STADE_TONES[stade]}
          />
        ))}
      </div>
    </>
  );
}

/** Ligne d'un stade : libellé + compte + barre (part du stade dans le total). */
function StadeLigne({
  libelle,
  nbBouteilles,
  pct,
  tone,
}: {
  libelle: string;
  nbBouteilles: number;
  pct: number;
  tone: ProgressTone;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          <span className="text-[14px] font-medium">{libelle}</span>
          <span className="text-[12px] text-champagne-700">{pct} % du stock</span>
        </div>
        <div className="mono tnum text-[15px] font-semibold">
          {formatNbBouteilles(nbBouteilles)}&nbsp;
          <span className="text-[12px] font-normal text-champagne-700">bt</span>
        </div>
      </div>
      <ProgressBar value={pct} tone={tone} label={`${libelle} : ${pct} %`} className="mt-2" />
    </div>
  );
}

/** Squelette de chargement : entête + 4 lignes de stade. */
function StockSkeleton() {
  return (
    <div className="space-y-5" aria-busy="true">
      <Skeleton variant="rect" height={72} />
      <Skeleton variant="text" lines={4} />
    </div>
  );
}
